import { useQuery } from '@tanstack/react-query';
import { api } from '../../api/client';
import type { AdminRole } from '../../auth/store';

// Types, query keys and small helpers shared by the Support inbox page and
// the sidebar badge in AppLayout.

export type SupportStatus = 'open' | 'waiting_user' | 'resolved';
export type SupportCategory = 'payment' | 'call' | 'account' | 'payout' | 'kyc' | 'other';
export type SupportSender = 'user' | 'admin' | 'system';

/** Roles the API lets into /admin/support. Finance is limited server-side to payment + payout. */
export const SUPPORT_ROLES: AdminRole[] = ['super_admin', 'ops', 'moderator', 'finance'];
export const FINANCE_CATEGORIES: SupportCategory[] = ['payment', 'payout'];

export interface AdminRef {
  id: string;
  name: string;
}

interface TicketBase {
  id: string;
  category: SupportCategory;
  status: SupportStatus;
  fromListenerMode: boolean;
  createdAt: string;
  lastMessageAt: string;
  firstReplyAt: string | null;
  resolvedAt: string | null;
  /** User messages support has not read yet. */
  unread: number;
  assignedAdmin: AdminRef | null;
}

export interface TicketPreview {
  sender: SupportSender;
  internal: boolean;
  text: string;
  createdAt: string;
}

export interface TicketRow extends TicketBase {
  /** displayName = the user's own Display Name — absent on older APIs. */
  user: { id: string; handle: string; displayName?: string | null };
  preview: TicketPreview | null;
}

export interface TicketPage {
  items: TicketRow[];
  nextBefore: string | null;
}

export interface SupportMessage {
  id: string;
  sender: SupportSender;
  /** Admin-only note; never shown to the user. */
  internal: boolean;
  admin: AdminRef | null;
  body: string;
  createdAt: string;
}

export interface RecentPayment {
  id: string;
  gateway: string;
  status: string;
  amountPaise: string;
  createdAt: string;
}

export interface RecentCall {
  id: string;
  mode: string;
  status: string;
  startedAt: string | null;
  endedAt: string | null;
}

export interface SupportUser {
  id: string;
  handle: string;
  /** The user's own Display Name — absent on older APIs. */
  displayName?: string | null;
  createdAt: string;
  deleted: boolean;
  listenerStatus: string | null;
  /** The user's COIN wallet balance (authoritative, whole coins). */
  walletBalanceCoins: number;
  recentPayments: RecentPayment[];
  recentCalls: RecentCall[];
}

export interface TicketDetail extends TicketBase {
  messages: SupportMessage[];
  user: SupportUser;
}

export interface SupportSummary {
  open: number;
  waitingUser: number;
  unread: number;
}

/**
 * Which inbox the support components work against. The Dude team's inbox sees
 * every request; an agency's sees only its own users' (the API scopes it), with
 * no internal notes, assignment or links into admin-only pages.
 */
export interface SupportScope {
  /** API base for tickets/summary. */
  base: string;
  /** Query-key prefix, so the two inboxes never share a cache entry. */
  keyPrefix: readonly string[];
  /** Agency inbox: reply + close only. */
  agency: boolean;
}

export const ADMIN_SUPPORT: SupportScope = { base: '/admin/support', keyPrefix: ['admin', 'support'], agency: false };
export const AGENCY_SUPPORT: SupportScope = {
  base: '/admin/agency-portal/support',
  keyPrefix: ['admin', 'agency-portal', 'support'],
  agency: true,
};

export const listKey = (s: SupportScope) => [...s.keyPrefix, 'tickets'] as const;
export const summaryKey = (s: SupportScope) => [...s.keyPrefix, 'summary'] as const;
export const ticketKey = (s: SupportScope, id: string) => [...s.keyPrefix, 'ticket', id] as const;

export const SUPPORT_LIST_KEY = ['admin', 'support', 'tickets'] as const;
export const SUPPORT_SUMMARY_KEY = ['admin', 'support', 'summary'] as const;
export const supportTicketKey = (id: string) => ['admin', 'support', 'ticket', id] as const;

export function useSupportSummary(opts: { enabled?: boolean; refetchInterval: number; scope?: SupportScope }) {
  const scope = opts.scope ?? ADMIN_SUPPORT;
  return useQuery({
    queryKey: summaryKey(scope),
    queryFn: async () => (await api.get<SupportSummary>(`${scope.base}/summary`)).data,
    enabled: opts.enabled ?? true,
    refetchInterval: opts.refetchInterval,
  });
}

export const STATUS_LABEL: Record<SupportStatus, string> = {
  open: 'Open',
  waiting_user: 'Waiting for user',
  resolved: 'Resolved',
};

export const STATUS_COLOR: Record<SupportStatus, string> = {
  open: 'error',
  waiting_user: 'processing',
  resolved: 'success',
};

export const CATEGORY_LABEL: Record<SupportCategory, string> = {
  payment: 'Payment',
  call: 'Call',
  account: 'Account',
  payout: 'Payout',
  kyc: 'KYC',
  other: 'Other',
};

export const CATEGORY_COLOR: Record<SupportCategory, string> = {
  payment: 'gold',
  call: 'blue',
  account: 'purple',
  payout: 'green',
  kyc: 'cyan',
  other: 'default',
};

export function errMsg(e: unknown, fallback: string) {
  return (e as { response?: { data?: { message?: string } } } | null)?.response?.data?.message ?? fallback;
}

/** Paise (as a decimal string from the API) → "₹1,234.50". */
export function rupees(paise: string) {
  return `₹${(Number(paise) / 100).toLocaleString('en-IN', {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  })}`;
}

export function ago(iso: string) {
  const secs = Math.max(0, Math.floor((Date.now() - Date.parse(iso)) / 1000));
  if (secs < 45) return 'just now';
  const mins = Math.floor(secs / 60);
  if (mins < 60) return `${Math.max(1, mins)}m ago`;
  const hours = Math.floor(mins / 60);
  if (hours < 24) return `${hours}h ago`;
  const days = Math.floor(hours / 24);
  if (days < 7) return `${days}d ago`;
  return new Date(iso).toLocaleDateString();
}

export function duration(startIso: string, endIso: string) {
  const secs = Math.max(0, Math.round((Date.parse(endIso) - Date.parse(startIso)) / 1000));
  const h = Math.floor(secs / 3600);
  const m = Math.floor((secs % 3600) / 60);
  const s = secs % 60;
  if (h > 0) return `${h}h ${m}m`;
  if (m > 0) return `${m}m ${s}s`;
  return `${s}s`;
}

export const dateTime = (iso: string) => new Date(iso).toLocaleString();

/** "22 Sept, 5:07 pm" — compact enough for a chat bubble. */
export const shortDateTime = (iso: string) =>
  new Date(iso).toLocaleString(undefined, { day: 'numeric', month: 'short', hour: 'numeric', minute: '2-digit' });
